import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Page, PageHeader, fmtEur, fmtEur2 } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Plus, ShieldCheck, Clock, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { InfissoQuickConfigurator } from "@/components/InfissoQuickConfigurator";
import ModalitaPagamentoPicker from "@/components/ModalitaPagamentoPicker";
import ListinoProdottoPicker from "@/components/ListinoProdottoPicker";
import MargineBox from "@/components/MargineBox";
import { useMarginParams, calcMargine, canSeeMarginDetails, RICARICO_STIMA } from "@/lib/margine";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const SOGLIA_SCONTO_AUTO = 5; // Venditore può applicare fino a 5% senza autorizzazione

export default function PreventivoComposite() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const vediDettagliMargine = canSeeMarginDetails(user);
  const marginParams = useMarginParams(isAdmin ? "semplice" : (user?.venditore_level || "semplice"));
  const [sections, setSections] = useState([]);
  const [cliente, setCliente] = useState({ nome: "", telefono: "", email: "", indirizzo: "" });
  const [mq, setMq] = useState(0);
  const [selections, setSelections] = useState({}); // voceId -> { qty }
  const [sicurezzaPct, setSicurezzaPct] = useState(3);
  const [direzionePct, setDirezionePct] = useState(5);
  const [sconto, setSconto] = useState(0);
  const [scontoPct, setScontoPct] = useState(0);
  const [ivaPct, setIvaPct] = useState(10);
  const [note, setNote] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [infissiExtras, setInfissiExtras] = useState([]); // [{id,name,qty,unit,price,unit_price,infisso_meta}]
  const [infissiModalOpen, setInfissiModalOpen] = useState(false);
  const [infissiEditIds, setInfissiEditIds] = useState(null); // ids degli infissi in edit (null = modalità add)
  const [scontoReq, setScontoReq] = useState(null);
  const [scontoDialog, setScontoDialog] = useState(false);
  const [scontoForm, setScontoForm] = useState({ pct: 10, motivo: "" });
  const [savedId, setSavedId] = useState(null);
  const [modalitaPagamento, setModalitaPagamento] = useState({ preset_id: "", label: "", rate: [] });
  // Selezioni da Listini Fornitori (porte/infissi/piastrelle/sanitari/...)
  const [listiniSelections, setListiniSelections] = useState([]);
  const [listinoPickerOpen, setListinoPickerOpen] = useState(false);
  const [listinoPickerCat, setListinoPickerCat] = useState("");
  // Tutti i prodotti dei listini fornitori per il sub-picker annidato nelle voci composite
  const [allProdottiFornitori, setAllProdottiFornitori] = useState([]);

  // Round 90: rilevamento preventivo accettato + commessa collegata
  const [prevStato, setPrevStato] = useState(null);
  const [prevCommessa, setPrevCommessa] = useState(null); // {id, codice} se già collegato a una commessa
  const [acceptedDialog, setAcceptedDialog] = useState(false);
  const [acceptedChoice, setAcceptedChoice] = useState(null); // 'extra' | 'nuovo' | 'modifica' | null
  const [cloning, setCloning] = useState(false);

  // Round 86: Voci extra manuali del venditore (non legate a sezioni)
  const [manualExtras, setManualExtras] = useState([]);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ name: "", category: "EXTRA", unit: "pz", qty: 1, price: 0, prezzo_acquisto: null, ricarico: null, save_to_backoffice: true });
  const [manualSimilar, setManualSimilar] = useState([]);
  const userRole = (user?.role || "").toLowerCase();
  const userLevel = (user?.venditore_level || "").toLowerCase();
  const canSaveToBackoffice = userRole === "admin" || userRole === "gestore" || (userRole === "venditore" && (userLevel === "responsabile" || userLevel === "area_manager"));

  // Mapping voce → categoria listino fornitori (per sub-picker annidato).
  // Match per nome voce, sezione e categoria (case-insensitive). L'ordine conta: regex più specifiche prima.
  const detectListinoCategoria = (voce, sectionId) => {
    const blob = `${voce.name || ""} ${voce.category || ""} ${sectionId || ""}`.toLowerCase();
    if (/(porta\s+blindat|porte\s+blindate|pannello\s+porta\s+blindata|blindat)/.test(blob)) return "porte_blindate";
    if (/(porta\s+intern|porte\s+intern|porta\s+battent|porta\s+scrign|porta\s+rasomur)/.test(blob)) return "porte_interne";
    if (/\bporta\b|\bporte\b/.test(blob)) return "porte_interne"; // fallback porte generiche
    if (/(piastrell|rivestiment|pavimento_gres|pavimento\s+gres|pavimentazione_gres|grè|gres)/.test(blob)) return "piastrelle";
    if (/(parquet|legno\s+pavim)/.test(blob)) return "parquet";
    if (/(sanitar|wc|lavabo|bidet|doccia|piatto\s+doccia|vasca)/.test(blob)) return "sanitari";
    if (/(rubinett|mixer|miscelator)/.test(blob)) return "rubinetteria";
    if (/(finestra|infiss|serrament)/.test(blob)) return "infissi";
    return null;
  };

  // Categorie sorelle che possiamo mostrare se la categoria principale ha 0 prodotti
  // (es. cerchi "porte interne" ma hai solo prodotti "porte blindate" → mostra entrambe)
  const SISTERS = {
    "porte_interne": ["porte_blindate"],
    "porte_blindate": ["porte_interne"],
    "piastrelle": ["parquet"],
    "parquet": ["piastrelle"],
  };

  // Prodotti raggruppati per categoria listino, ordinati per prezzo crescente
  const prodottiPerCategoria = useMemo(() => {
    const out = {};
    allProdottiFornitori.forEach(p => {
      const c = (p.categoria || "").toLowerCase();
      if (!out[c]) out[c] = [];
      out[c].push(p);
    });
    Object.values(out).forEach(arr => arr.sort((a, b) => (a.prezzo_rivendita || 0) - (b.prezzo_rivendita || 0)));
    return out;
  }, [allProdottiFornitori]);

  const onInfissiConfirm = ({ items, mode, originalIds }) => {
    const rows = items.map((it, i) => ({
      id: `infisso-${Date.now()}-${i}`,
      name: `${it.tipologia_name || "Infisso"} ${it.larghezza}×${it.altezza}cm · ${it.materiale_name} ${it.vetro_name} (${it.ante} ante)${it.tapparella ? " + tapparella" : ""}${it.zanzariera ? " + zanzariera" : ""}`,
      unit: "pz", qty: it.qty || 1,
      unit_price: Math.round((it.price || 0) / (it.qty || 1)),
      price: it.price,
      infisso_meta: it,
    }));
    if (mode === "edit" && originalIds && originalIds.length) {
      // Sostituisci gli infissi originali con quelli modificati (preservando l'id se solo 1)
      setInfissiExtras((arr) => {
        const remaining = arr.filter((r) => !originalIds.includes(r.id));
        // Se 1:1, riusa l'id originale per non perdere riferimenti UI
        if (rows.length === originalIds.length && rows.length === 1) {
          rows[0].id = originalIds[0];
        }
        return [...remaining, ...rows];
      });
      toast.success(rows.length === 1 ? "Infisso aggiornato" : `${rows.length} infissi aggiornati`);
    } else {
      setInfissiExtras((arr) => [...arr, ...rows]);
      toast.success(`${items.length} infissi aggiunti`);
    }
    setInfissiEditIds(null);
  };
  const removeInfisso = (id) => setInfissiExtras((arr) => arr.filter((r) => r.id !== id));
  const editInfisso = (id) => {
    setInfissiEditIds([id]);
    setInfissiModalOpen(true);
  };
  const updateInfissoField = (id, field, value) => {
    setInfissiExtras((arr) => arr.map((r) => {
      if (r.id !== id) return r;
      const meta = { ...(r.infisso_meta || {}) };
      meta[field] = value;
      // Ricalcolo prezzo on-the-fly per misure/qty/accessori inline
      const next = { ...r, infisso_meta: meta };
      if (field === "qty") next.qty = Math.max(1, Number(value) || 1);
      // Per ricalcolo del prezzo serve InfissoQuickConfigurator (vivono lì le formule); qui aggiorniamo solo:
      // - se cambia qty: scala price (price = unit_price * qty)
      if (field === "qty") next.price = Math.round((next.unit_price || 0) * next.qty);
      return next;
    }));
  };
  const infissiTot = useMemo(() => infissiExtras.reduce((s, r) => s + (r.price || 0), 0), [infissiExtras]);

  useEffect(() => {
    api.get("/composite-sections").then((r) => { setSections(r.data); if (r.data[0]) setActiveSection(r.data[0].id); });
    // Carica TUTTI i prodotti dei listini fornitori per il sub-picker annidato
    api.get("/fornitori-listini-prodotti/cerca?max_results=2000")
      .then(r => setAllProdottiFornitori(r.data || []))
      .catch(() => setAllProdottiFornitori([]));
    // Round 90 septdecies: reset esplicito dello stato quando si apre un NUOVO preventivo
    // (fix: a volte apparivano "extra" ereditati da un preventivo aperto precedentemente).
    if (isNew) {
      setCliente({ nome: "", telefono: "", email: "", indirizzo: "" });
      setMq(0);
      setSelections({});
      setSicurezzaPct(3);
      setDirezionePct(5);
      setSconto(0);
      setScontoPct(0);
      setIvaPct(10);
      setNote("");
      setManualExtras([]);
      setInfissiExtras([]);
      setListiniSelections([]);
      setSavedId(null);
      setPrevStato(null);
      setPrevCommessa(null);
      setAcceptedChoice(null);
      setAcceptedDialog(false);
      setScontoReq(null);
      setModalitaPagamento({ preset_id: "", label: "", rate: [] });
      // Prefill da "Importa preventivo concorrente" (?prefill=1)
      if (new URLSearchParams(window.location.search).get("prefill") === "1") {
        try {
          const pf = JSON.parse(sessionStorage.getItem("composite_prefill") || "null");
          if (pf) {
            setMq(pf.mq || 0); setSelections(pf.selections || {}); setManualExtras(pf.manual_extras || []); setNote(pf.note || "");
          }
        } catch (_) { /* prefill non disponibile */ }
      }
    }
    if (!isNew) {
      api.get(`/preventivi/${id}`).then((r) => {
        const d = r.data;
        setCliente(d.cliente || {}); setMq(d.mq || 0); setNote(d.note || "");
        setSconto(d.sconto_eur || 0); setScontoPct(d.sconto_pct || 0); setIvaPct(d.iva_pct || 10);
        setSavedId(d.id);
        setPrevStato(d.stato || null);
        // Se il preventivo è già stato accettato, cerchiamo la commessa collegata (per proporre "Extra")
        if ((d.stato || "").toLowerCase() === "accettato" && !acceptedChoice) {
          (async () => {
            let com = null;
            try {
              const all = await api.get("/commesse").catch(() => ({ data: [] }));
              const list = all.data || [];
              if (d.commessa_id) com = list.find((c) => c.id === d.commessa_id) || null;
              if (!com) com = list.find((c) => c.preventivo_id === d.id) || null;
            } catch (_) { /* silent */ }
            setPrevCommessa(com ? { id: com.id, codice: com.codice || com.id } : null);
            setAcceptedDialog(true);
          })();
        }
        // Carica eventuale richiesta sconto attiva
        api.get(`/sconto-richieste`).then(sr => {
          const rows = (sr.data || []).filter(rr => rr.preventivo_id === d.id);
          if (rows.length) setScontoReq(rows[0]);
        }).catch(() => {});
        setSicurezzaPct(d.sicurezza_pct ?? 3); setDirezionePct(d.direzione_lavori_pct ?? 5);
        setModalitaPagamento(d.modalita_pagamento || { preset_id: "", label: "", rate: [] });
        setListiniSelections(d.listini_selections || []);
        const sel = {};
        (d.composite_selections || []).forEach((s) => {
          sel[s.voce_id] = {
            qty: s.qty, price: s.price,
            product_id: s.product_id || null,
            product_nome: s.product_nome || null,
            product_fornitore: s.product_fornitore || null,
            listino_id: s.listino_id || null,
          };
        });
        setSelections(sel);
        setManualExtras(d.manual_extras || []);
        setInfissiExtras(d.infissi_extras || []);
        // R87: avviso se totale salvato discorda dal ricalcolo (es. dopo restore audit con voci mancanti)
        const savedTotal = Number(d.totale_iva_incl || 0);
        if (savedTotal > 0) {
          // Calcolo veloce per check (stessa formula della pagina)
          const vTot = (d.composite_selections || []).reduce((s, v) => s + ((v.qty || 0) * (v.price || 0)), 0);
          const lTot = (d.listini_selections || []).reduce((s, p) => s + ((parseFloat(p.qty) || 0) * (parseFloat(p.prezzo_rivendita) || 0)), 0);
          const mTot = (d.manual_extras || []).reduce((s, m) => s + ((Number(m.qty) || 0) * (Number(m.price) || 0)), 0);
          const iTot = (d.infissi_extras || []).reduce((s, x) => s + ((x.qty || 0) * (x.unit_price || x.price || 0)), 0);
          const m = parseFloat(d.mq || 0);
          let mult = 1;
          if (m > 0 && m < 40) mult = 1.15; else if (m > 0 && m < 60) mult = 1.10;
          const base = vTot + lTot + mTot;
          const sic = base * ((d.sicurezza_pct || 0) / 100);
          const dir = base * ((d.direzione_lavori_pct || 0) / 100);
          const preSc = base * mult + iTot + sic + dir - (d.sconto_eur || 0);
          const scPct = preSc * ((d.sconto_pct || 0) / 100);
          const imp = preSc - scPct;
          const iv = imp * ((d.iva_pct || 10) / 100);
          const ricalc = imp + iv;
          if (Math.abs(savedTotal - ricalc) > 100) {
            toast.warning(
              `⚠ Discrepanza totale: salvato € ${savedTotal.toLocaleString("it-IT")} vs ricalcolato € ${ricalc.toLocaleString("it-IT")}. Le voci visibili non corrispondono al totale del DB (es. preventivo ripristinato). Modifica le voci per allineare, poi salva.`,
              { duration: 10000 }
            );
          }
        }
      });
    }
  }, [id, isNew]);

  const totaleVoci = useMemo(() => {
    let total = 0;
    sections.forEach((s) => {
      s.voci.forEach((v) => {
        const sel = selections[v.id];
        if (sel && sel.qty > 0) {
          // Se voce modificabile + il venditore ha messo un prezzo custom, usa quello; altrimenti listino
          const effPrice = (v.modificabile_dal_venditore && typeof sel.price === "number" && sel.price >= 0) ? sel.price : v.price;
          total += (sel.qty || 0) * effPrice;
        }
      });
    });
    return total;
  }, [sections, selections]);

  const totaleListini = useMemo(() => listiniSelections.reduce((s, p) => s + ((parseFloat(p.qty) || 0) * (parseFloat(p.prezzo_rivendita) || 0)), 0), [listiniSelections]);

  // Subtotale voci manuali (Round 86)
  const totaleManuali = useMemo(() => manualExtras.reduce((s, m) => s + ((Number(m.qty) || 0) * (Number(m.price) || 0)), 0), [manualExtras]);

  // Costo diretto reale = sommatoria di prezzi_acquisto reali (voci + listini + infissi + manuali).
  // Se prezzo_acquisto non è impostato, usa il fallback price/1.6 (marginalità stimata).
  const costoDirettoBreakdown = useMemo(() => {
    let voci_bo = 0, listini = 0, infissi = 0, manuali = 0;
    let manuali_stimati = false;
    sections.forEach((s) => {
      s.voci.forEach((v) => {
        const sel = selections[v.id];
        if (sel && sel.qty > 0) {
          const pa = Number(v.prezzo_acquisto) || 0;
          const cost = pa > 0 ? pa : (Number(v.price) || 0) / RICARICO_STIMA;
          voci_bo += (sel.qty || 0) * cost;
        }
      });
    });
    listiniSelections.forEach((p) => {
      const qty = parseFloat(p.qty) || 0;
      const netto = Number(p.prezzo_netto) || Number(p.netto) || (Number(p.prezzo_rivendita) || 0) / RICARICO_STIMA;
      listini += qty * netto;
    });
    infissiExtras.forEach((i) => {
      const pa = Number(i.prezzo_acquisto) || 0;
      const cost = pa > 0 ? pa : (Number(i.price) || 0) / RICARICO_STIMA;
      infissi += cost;
    });
    manualExtras.forEach((m) => {
      const pa = Number(m.prezzo_acquisto) || 0;
      if (pa <= 0 && (Number(m.price) || 0) > 0) manuali_stimati = true;
      const cost = pa > 0 ? pa : (Number(m.price) || 0) / RICARICO_STIMA;
      manuali += (Number(m.qty) || 0) * cost;
    });
    return {
      voci_bo: Math.round(voci_bo),
      listini: Math.round(listini),
      infissi: Math.round(infissi),
      manuali: Math.round(manuali),
      manuali_stimati,
      totale: Math.round(voci_bo + listini + infissi + manuali),
    };
  }, [sections, selections, listiniSelections, infissiExtras, manualExtras]);
  const costoDirettoReale = costoDirettoBreakdown.totale;

  const sicurezzaAmt = (totaleVoci + totaleListini + totaleManuali) * (sicurezzaPct / 100);
  const direzioneAmt = (totaleVoci + totaleListini + totaleManuali) * (direzionePct / 100);
  // Maggiorazione mq piccole: <40 a corpo (×1.15 e mq min 40), <60 +10%
  const mqAdj = useMemo(() => {
    const m = parseFloat(mq || 0);
    if (!m) return { multiplier: 1, mode: "normal" };
    if (m < 40) return { multiplier: 1.15, mode: "a_corpo" };
    if (m < 60) return { multiplier: 1.10, mode: "maggiorato" };
    return { multiplier: 1, mode: "normal" };
  }, [mq]);
  // Applica maggiorazione al totale voci+listini+manuali, non a infissi che sono extra fissi
  const totaleVociMaggiorato = (totaleVoci + totaleListini + totaleManuali) * mqAdj.multiplier;
  const imponibilePreScontoPct = totaleVociMaggiorato + infissiTot + sicurezzaAmt + direzioneAmt - (sconto || 0);
  const scontoPctAmt = imponibilePreScontoPct * (scontoPct || 0) / 100;
  const imponibile = imponibilePreScontoPct - scontoPctAmt;
  const iva = imponibile * (ivaPct / 100);
  const totale = imponibile + iva;

  // Round 90 septdecies: margine stimato live. Costi diretti ≈ imponibile_pre_sconto / 1.8
  // (ricarico medio). Sotto 30% → serve autorizzazione admin per lo sconto.
  // Margine unico (costi diretti reali + costi indiretti): vedi lib/margine.js
  const margine = calcMargine(imponibile, costoDirettoReale, marginParams);
  const margineBreakdown = [
    { nome: "Voci backoffice", importo: costoDirettoBreakdown.voci_bo },
    { nome: "Listini fornitori", importo: costoDirettoBreakdown.listini },
    { nome: "Infissi", importo: costoDirettoBreakdown.infissi },
    { nome: "Voci manuali", importo: costoDirettoBreakdown.manuali, stimato: costoDirettoBreakdown.manuali_stimati },
  ];

  const save = async () => {
    if (!cliente.nome) return toast.error("Inserisci nome cliente");
    // Round 90: guard sul preventivo già accettato — se l'utente non ha scelto ancora, riapri dialog
    if (!isNew && (prevStato || "").toLowerCase() === "accettato" && acceptedChoice == null) {
      setAcceptedDialog(true);
      toast.warning("Preventivo già accettato: scegli come procedere prima di salvare");
      return;
    }
    const comp = [];
    sections.forEach((s) => s.voci.forEach((v) => {
      const sel = selections[v.id];
      if (sel && sel.qty > 0) {
        const effPrice = (v.modificabile_dal_venditore && typeof sel.price === "number" && sel.price >= 0) ? sel.price : (sel.product_id ? sel.price : v.price);
        comp.push({
          section_id: s.id, voce_id: v.id, name: v.name, unit: v.unit, price: effPrice, list_price: v.price,
          qty: sel.qty, modificabile_dal_venditore: !!v.modificabile_dal_venditore,
          // Prodotto fornitore associato (se scelto da sub-picker annidato)
          product_id: sel.product_id || null,
          product_nome: sel.product_nome || null,
          product_fornitore: sel.product_fornitore || null,
          listino_id: sel.listino_id || null,
        });
      }
    }));
    const payload = {
      tipo: "composite", cliente, mq, composite_selections: comp,
      manual_extras: manualExtras,
      infissi_extras: infissiExtras,
      sicurezza_pct: sicurezzaPct, direzione_lavori_pct: direzionePct,
      sconto_eur: sconto, sconto_pct: scontoPct, iva_pct: ivaPct, note,
      mq_adjustment_mode: mqAdj.mode, mq_multiplier: mqAdj.multiplier,
      totale_iva_incl: totale, totale_iva_escl: imponibile,
      modalita_pagamento: modalitaPagamento,
      listini_selections: listiniSelections,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/preventivi", payload);
        setSavedId(data.id);
        try { sessionStorage.removeItem("composite_prefill"); } catch (_) {}
        toast.success("Preventivo salvato"); nav(`/preventivocomposite/${data.id}`, { replace: true });
      } else {
        await api.put(`/preventivi/${id}`, payload); toast.success("Aggiornato");
      }
    } catch (e) {
      console.error("[PreventivoComposite] save error:", e);
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const sec = sections.find((s) => s.id === activeSection);

  return (
    <div>
      <PageHeader title="Preventivo Composite" subtitle="Configura la tua ristrutturazione pezzo per pezzo"
        actions={<div className="flex gap-4 items-center text-right flex-wrap justify-end">
          {!isNew && (
            <div>
              <div className="text-[10px] uppercase text-zinc-500">Stato</div>
              <select
                value={prevStato || "bozza"}
                onChange={async (e) => {
                  const s = e.target.value;
                  if (s === prevStato) return;
                  if (!window.confirm(`Cambiare stato preventivo a "${s}"?${s === "accettato" ? "\nVerrà generata automaticamente la commessa cantiere." : ""}`)) return;
                  try {
                    const { data } = await api.patch(`/preventivi/${savedId || id}/stato`, { stato: s });
                    setPrevStato(s);
                    toast.success(`Stato → ${s}${data.auto_commessa ? " · Commessa creata" : ""}`);
                  } catch (err) { toast.error(err?.response?.data?.detail || "Errore cambio stato"); }
                }}
                className={`h-8 text-xs px-2 rounded border cursor-pointer font-semibold ${
                  prevStato === "accettato" ? "bg-emerald-50 text-emerald-800 border-emerald-300" :
                  prevStato === "rifiutato" ? "bg-rose-50 text-rose-800 border-rose-300" :
                  prevStato === "inviato" ? "bg-blue-50 text-blue-800 border-blue-300" :
                  "bg-zinc-50 text-zinc-700 border-zinc-300"
                }`}
                data-testid="comp-header-stato"
              >
                <option value="bozza">📝 Bozza</option>
                <option value="inviato">📤 Inviato</option>
                <option value="accettato">✅ Accettato</option>
                <option value="rifiutato">❌ Rifiutato</option>
              </select>
            </div>
          )}
          <div><div className="text-[10px] uppercase text-zinc-500">MQ</div><Input type="number" min={0} step="0.5" className="w-20 h-8" value={mq} onChange={(e) => setMq(Math.max(0, Number(e.target.value) || 0))} /></div>
          <div><div className="text-[10px] uppercase text-zinc-500">€/MQ</div><div className="text-sm font-mono">{mq ? fmtEur2(totaleVoci / mq) : "0.00 €"}</div></div>
          <div><div className="text-[10px] uppercase text-zinc-500">Sicurezza {sicurezzaPct}%</div><div className="text-sm font-mono">{fmtEur(sicurezzaAmt)}</div></div>
          <div><div className="text-[10px] uppercase text-zinc-500">Dir. Lav {direzionePct}%</div><div className="text-sm font-mono">{fmtEur(direzioneAmt)}</div></div>
          <div><div className="text-[10px] uppercase text-zinc-500">Totale IVA Incl.</div><div className="text-lg font-bold" data-testid="totale-composite">{fmtEur2(totale)}</div></div>
          <Button onClick={() => { setInfissiEditIds(null); setInfissiModalOpen(true); }} variant="outline" size="sm" className="rounded-sm" data-testid="comp-add-infissi-top-btn">
            <Plus className="h-4 w-4 mr-1" />Infissi {infissiExtras.length > 0 && `(${infissiExtras.length})`}
          </Button>
        </div>} />
      {/* Round 90: banner preventivo accettato — mostra scelta corrente e permette di riaprire dialog */}
      {(prevStato || "").toLowerCase() === "accettato" && !isNew && (
        <div className={`mx-auto max-w-6xl mt-3 mb-2 border-2 rounded-lg p-3 flex items-center justify-between gap-3 ${acceptedChoice === "modifica" ? "bg-rose-50 border-rose-300" : acceptedChoice === "extra" ? "bg-amber-50 border-amber-300" : "bg-blue-50 border-blue-300"}`} data-testid="comp-accepted-banner">
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className={`h-5 w-5 ${acceptedChoice === "modifica" ? "text-rose-600" : "text-amber-600"}`} />
            <div>
              {acceptedChoice === "modifica" && <><strong className="text-rose-800">Attenzione:</strong> stai modificando un preventivo <strong>già accettato</strong>. Il salvataggio sovrascrive il documento firmato.</>}
              {acceptedChoice === "extra" && <><strong className="text-amber-800">Preventivo extra (variante):</strong> le modifiche saranno salvate come nuovo preventivo <strong>Extra</strong> collegato alla stessa commessa{prevCommessa ? ` ${prevCommessa.codice}` : ""}.</>}
              {acceptedChoice == null && <><strong>Preventivo accettato.</strong> Scegli come procedere prima di modificarlo.</>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAcceptedDialog(true)} data-testid="comp-accepted-reopen">Cambia scelta</Button>
        </div>
      )}
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
          <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-lg p-3">
            <div className="mb-3"><Label className="text-xs">Dati Cliente</Label>
              <Input placeholder="Nome *" className="mt-1" value={cliente.nome} onChange={(e) => setCliente({ ...cliente, nome: e.target.value })} data-testid="comp-nome" />
              <Input placeholder="Telefono" className="mt-1" value={cliente.telefono} onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })} />
              <Input placeholder="Email" className="mt-1" value={cliente.email} onChange={(e) => setCliente({ ...cliente, email: e.target.value })} />
              <Input placeholder="Indirizzo" className="mt-1" value={cliente.indirizzo} onChange={(e) => setCliente({ ...cliente, indirizzo: e.target.value })} />
            </div>
            {sections.length === 0 ? (
              <div className="text-xs text-zinc-500 italic px-2 py-3 border border-dashed border-zinc-300 rounded">
                Caricamento sezioni in corso… Se non appaiono, controlla che il backoffice contenga le voci.
              </div>
            ) : (
              <div className="space-y-1">
                {sections.map((s) => {
                  const count = s.voci.filter((v) => selections[v.id]?.qty > 0).length;
                  const tot = s.voci.reduce((acc, v) => acc + (selections[v.id]?.qty || 0) * v.price, 0);
                  return (
                    <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`comp-sec-${s.id}`}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${activeSection === s.id ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}>
                      <div className="flex items-center justify-between">
                        <span>{s.name}</span>
                        {count > 0 && <span className="text-xs opacity-70">{count}</span>}
                      </div>
                      {tot > 0 && <div className="text-[10px] font-mono opacity-60">{fmtEur(tot)}</div>}
                    </button>
                  );
                })}
                <button onClick={() => setActiveSection("__infissi__")} data-testid="comp-sec-infissi"
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors mt-2 border-t border-zinc-200 pt-3 ${activeSection === "__infissi__" ? "bg-amber-600 text-white" : "hover:bg-amber-50 text-amber-700"}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><Plus className="h-3 w-3" />Infissi (extra configuratore)</span>
                    {infissiExtras.length > 0 && <span className="text-xs opacity-70">{infissiExtras.length}</span>}
                  </div>
                  {infissiTot > 0 && <div className="text-[10px] font-mono opacity-60">{fmtEur(infissiTot)}</div>}
                </button>
                <button onClick={() => setActiveSection("__listini__")} data-testid="comp-sec-listini"
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${activeSection === "__listini__" ? "bg-blue-600 text-white" : "hover:bg-blue-50 text-blue-700"}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5">🛒 Listini fornitori</span>
                    {listiniSelections.length > 0 && <span className="text-xs opacity-70">{listiniSelections.length}</span>}
                  </div>
                  {totaleListini > 0 && <div className="text-[10px] font-mono opacity-60">{fmtEur(totaleListini)}</div>}
                </button>
                <button onClick={() => setActiveSection("__manual__")} data-testid="comp-sec-manual"
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${activeSection === "__manual__" ? "bg-emerald-600 text-white" : "hover:bg-emerald-50 text-emerald-700"}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><Plus className="h-3 w-3" />Voci extra manuali</span>
                    {manualExtras.length > 0 && <span className="text-xs opacity-70">{manualExtras.length}</span>}
                  </div>
                  {manualExtras.length > 0 && <div className="text-[10px] font-mono opacity-60">{fmtEur(manualExtras.reduce((s, x) => s + (Number(x.qty) || 0) * (Number(x.price) || 0), 0))}</div>}
                </button>
              </div>
            )}
            {/* Banner maggiorazione mq piccole */}
            {mqAdj.mode !== "normal" && (
              <div className={`mt-3 p-2.5 rounded border-l-4 text-xs ${mqAdj.mode === "a_corpo" ? "bg-rose-50 border-rose-500 text-rose-900" : "bg-amber-50 border-amber-500 text-amber-900"}`} data-testid="comp-mq-banner">
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>{mqAdj.mode === "a_corpo" ? "Modalità A CORPO" : "+10% mq piccole"}</strong>
                    <div className="mt-0.5 leading-snug">
                      {mqAdj.mode === "a_corpo" ? "Sotto i 40 m² i costi fissi non scalano: applico +15% sul totale voci." : "Sotto i 60 m² il totale voci è maggiorato del 10%."}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Sezione SCONTO con autorizzazione */}
            <div className="mt-3 p-2.5 border border-zinc-200 rounded bg-zinc-50">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-widest text-zinc-700">Sconto %</Label>
                {scontoReq && scontoReq.stato === "pending" && <span className="text-[9px] mono uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" /> in attesa</span>}
                {scontoReq && scontoReq.stato === "approvato" && <span className="text-[9px] mono uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded flex items-center gap-0.5"><ShieldCheck className="h-2.5 w-2.5" /> {scontoReq.pct_approvato || scontoReq.pct_richiesto}% OK</span>}
              </div>
              <Input
                type="number" min={0} max={100} step="0.5" value={scontoPct}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                  // Admin libero
                  if (isAdmin) { setScontoPct(v); return; }
                  // Venditore: cap duro al 5% (soglia operativa) e verifica margine 30%
                  if (v > SOGLIA_SCONTO_AUTO) {
                    setScontoPct(SOGLIA_SCONTO_AUTO);
                    setScontoForm({ pct: v, motivo: scontoReq?.motivo || "" });
                    setScontoDialog(true);
                    toast.info(`Sconto > ${SOGLIA_SCONTO_AUTO}%: serve autorizzazione admin`);
                    return;
                  }
                  // Simula l'applicazione e blocca se farebbe scendere il margine sotto 30%
                  const sim = calcMargine(imponibilePreScontoPct * (1 - v / 100), costoDirettoReale, marginParams);
                  if (sim && sim.pct < sim.soglia) {
                    setScontoPct(scontoPct); // non applicare
                    setScontoForm({ pct: v, motivo: scontoReq?.motivo || `Sconto ${v}%: margine sotto il minimo` });
                    setScontoDialog(true);
                    toast.warning("Sconto bloccato: il margine scenderebbe sotto il minimo consentito. Serve autorizzazione admin.");
                    return;
                  }
                  setScontoPct(v);
                }}
                className="h-8 mono" data-testid="comp-sconto-pct"
              />
              {/* Round 90 septdecies: badge margine finale sempre visibile */}
              <div className="mt-1.5"><MargineBox m={margine} details={vediDettagliMargine} breakdown={margineBreakdown} testId="comp-margine" /></div>
              <div className="text-[10px] text-zinc-500 mt-1">
                {isAdmin ? "Admin: libero." : `Venditore: fino al ${SOGLIA_SCONTO_AUTO}% diretto`}
              </div>
              {!isAdmin && (
                <Button variant="outline" size="sm" className="mt-1.5 w-full h-7 text-[11px]" onClick={() => { setScontoForm({ pct: Math.max(scontoPct, SOGLIA_SCONTO_AUTO + 1), motivo: scontoReq?.motivo || "" }); setScontoDialog(true); }} disabled={!savedId && isNew} data-testid="comp-sconto-richiedi">
                  Richiedi sconto maggiore
                </Button>
              )}
            </div>
          </div>

          <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-5">
            {activeSection === "__listini__" ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">🛒 Prodotti da Listini Fornitori</h3>
                    <p className="text-xs text-zinc-500">Aggiungi porte, infissi, piastrelle, sanitari, ecc. dai listini caricati in admin. I prezzi sono già a rivendita (netto×ricarico).</p>
                  </div>
                  <Button size="sm" onClick={() => { setListinoPickerCat(""); setListinoPickerOpen(true); }} data-testid="comp-listini-add-btn" style={{ background: "var(--brand)", color: "white" }}>
                    <Plus className="h-4 w-4 mr-1" />Aggiungi prodotti
                  </Button>
                </div>
                {listiniSelections.length === 0 ? (
                  <div className="text-zinc-500 text-center py-12">Nessun prodotto selezionato.<br/><span className="text-xs">Clicca "Aggiungi prodotti" per scegliere dai listini fornitori (porte, piastrelle, sanitari, ecc.).</span></div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Prodotto</th>
                        <th className="px-3 py-2 text-left">Fornitore</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-right w-24">€/u</th>
                        <th className="px-3 py-2 text-right w-28">Totale</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {listiniSelections.map((p, i) => (
                        <tr key={`${p.listino_id}-${p.id}-${i}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-xs">{p.nome}</div>
                            <div className="text-[10px] text-zinc-500">{p.codice ? `[${p.codice}] ` : ""}{p.categoria_dettaglio || p.categoria} · {p.unit}</div>
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-600">{p.fornitore_nome}</td>
                          <td className="px-3 py-2"><Input type="number" min={0.01} step="0.01" value={p.qty} onChange={e => {
                            const q = parseFloat(e.target.value) || 0;
                            setListiniSelections(ls => ls.map((x, j) => j === i ? { ...x, qty: q, importo: q * (x.prezzo_rivendita || 0) } : x));
                          }} className="h-8 text-xs text-right mono" data-testid={`comp-listino-qty-${i}`} /></td>
                          <td className="px-3 py-2 text-right mono text-xs">€ {(p.prezzo_rivendita || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right mono font-bold">€ {((p.qty || 0) * (p.prezzo_rivendita || 0)).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right"><button onClick={() => setListiniSelections(ls => ls.filter((_, j) => j !== i))} className="text-rose-600 text-xs" data-testid={`comp-listino-del-${i}`}>×</button></td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-bold">
                        <td colSpan={4} className="px-3 py-2 text-right">Subtotale listini</td>
                        <td className="px-3 py-2 text-right mono" data-testid="comp-listini-total">€ {totaleListini.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </>
            ) : activeSection === "__infissi__" ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Infissi · Configuratore extra</h3>
                  <Button size="sm" onClick={() => { setInfissiEditIds(null); setInfissiModalOpen(true); }} data-testid="comp-add-infissi-btn" style={{ background: "var(--brand)", color: "white" }}>
                    <Plus className="h-4 w-4 mr-1" />Aggiungi infissi
                  </Button>
                </div>
                {infissiExtras.length === 0 ? (
                  <div className="text-zinc-500 text-center py-12">Nessun infisso configurato. Clicca "Aggiungi infissi" per aprire il configuratore.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Descrizione</th>
                        <th className="px-3 py-2 text-center w-32">Accessori</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-right w-28">€/u</th>
                        <th className="px-3 py-2 text-right w-28">Totale</th>
                        <th className="w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {infissiExtras.map((r) => {
                        const meta = r.infisso_meta || {};
                        return (
                          <tr key={r.id} data-testid={`comp-inf-row-${r.id}`}>
                            <td className="px-3 py-2">
                              <div className="font-medium text-xs">{r.name}</div>
                              {(meta.larghezza || meta.altezza) && (
                                <div className="text-[10px] text-zinc-500 mt-0.5">
                                  Misure: <strong className="font-mono">{meta.larghezza}×{meta.altezza} cm</strong>
                                  {meta.colore && <> · Colore: {meta.colore}</>}
                                  {meta.materiale_name && <> · {meta.materiale_name}</>}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex flex-col gap-1 items-center text-[10px]">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!meta.tapparella}
                                    onChange={(e) => updateInfissoField(r.id, "tapparella", e.target.checked)}
                                    className="h-3.5 w-3.5"
                                    data-testid={`comp-inf-tapp-${r.id}`}
                                  /> Tapparella
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!meta.zanzariera}
                                    onChange={(e) => updateInfissoField(r.id, "zanzariera", e.target.checked)}
                                    className="h-3.5 w-3.5"
                                    data-testid={`comp-inf-zanz-${r.id}`}
                                  /> Zanzariera
                                </label>
                                {(meta.tapparella || meta.zanzariera) && (
                                  <div className="text-[9px] text-amber-600 italic">↪ ricalcola prezzo con "Modifica"</div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Input
                                type="number" min={1} step="1"
                                className="h-8 w-16 text-right font-mono"
                                value={r.qty}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => updateInfissoField(r.id, "qty", e.target.value === "" ? "" : Number(e.target.value))}
                                onBlur={(e) => updateInfissoField(r.id, "qty", Math.max(1, Number(e.target.value) || 1))}
                                data-testid={`comp-inf-qty-${r.id}`}
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{fmtEur2(r.unit_price)}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{fmtEur2(r.price)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="inline-flex gap-1">
                                <button
                                  onClick={() => editInfisso(r.id)}
                                  className="px-2 py-1 rounded text-[11px] font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300"
                                  title="Modifica misure / accessori / materiale"
                                  data-testid={`comp-inf-edit-${r.id}`}
                                >✏️ Modifica</button>
                                <button
                                  onClick={() => removeInfisso(r.id)}
                                  className="px-2 py-1 rounded text-rose-600 hover:bg-rose-50 text-[11px]"
                                  title="Rimuovi infisso"
                                  data-testid={`comp-inf-del-${r.id}`}
                                >×</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-amber-50 font-bold">
                        <td colSpan={4} className="px-3 py-2 text-right">Subtotale infissi</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtEur2(infissiTot)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <div className="mt-3 text-[11px] text-zinc-500 italic">
                  💡 Per modificare misure, materiale, vetro, n° ante o ricalcolare il prezzo automaticamente, usa <strong>"✏️ Modifica"</strong> sulla riga: si riapre il configuratore con i dati pre-compilati.
                </div>
              </>
            ) : activeSection === "__manual__" ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Voci extra manuali</h3>
                  <Button size="sm" onClick={() => { setManualForm({ name: "", category: "EXTRA", unit: "pz", qty: 1, price: 0, prezzo_acquisto: null, ricarico: null, save_to_backoffice: canSaveToBackoffice }); setManualSimilar([]); setManualDialogOpen(true); }} data-testid="comp-add-manual-btn" style={{ background: "var(--brand)", color: "white" }}>
                    <Plus className="h-4 w-4 mr-1" />Aggiungi voce manuale
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Aggiungi qui qualsiasi voce extra che non trovi nel listino opere o nei listini fornitori (es. una lavorazione speciale, un materiale particolare, una pratica burocratica non standard).
                  {canSaveToBackoffice && <> Se non esiste, potrai salvarla anche nel <strong>Listino Opere (Voci Backoffice)</strong> per riusarla nei prossimi preventivi.</>}
                </p>
                {manualExtras.length === 0 ? (
                  <div className="text-zinc-500 text-center py-8 italic">Nessuna voce manuale aggiunta.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Descrizione</th>
                        <th className="px-3 py-2 text-left w-28">Categoria</th>
                        <th className="px-3 py-2 text-right w-20">Qty</th>
                        <th className="px-3 py-2 text-right w-14">U.M.</th>
                        {vediDettagliMargine && <th className="px-3 py-2 text-right w-24 bg-amber-50">Prezzo acquisto</th>}
                        {vediDettagliMargine && <th className="px-3 py-2 text-right w-16 bg-amber-50">Ricarico</th>}
                        <th className="px-3 py-2 text-right w-24">Prezzo vendita</th>
                        <th className="px-3 py-2 text-right w-24">Totale</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {manualExtras.map((m, i) => {
                        const pa = Number(m.prezzo_acquisto) || 0;
                        const ric = Number(m.ricarico) || 0;
                        // Se prezzo_acquisto+ricarico impostati, il prezzo di vendita si calcola live (se l'utente non l'ha già impostato manualmente)
                        return (
                        <tr key={i} data-testid={`comp-manual-row-${i}`}>
                          <td className="px-3 py-2">
                            <div>{m.name}</div>
                            {m.saved_to_backoffice && <div className="text-[9px] text-emerald-700">✓ Salvata nelle Voci Backoffice</div>}
                            {vediDettagliMargine && pa > 0 && (Number(m.price) || 0) > 0 && (
                              <div className="text-[9px] text-emerald-600 mono">
                                Margine unitario: {fmtEur2((Number(m.price) || 0) - pa)} ({(((Number(m.price) - pa) / (Number(m.price) || 1)) * 100).toFixed(1)}%)
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs">{m.category || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            <Input type="number" min={0} step="0.01" value={m.qty}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setManualExtras(manualExtras.map((x, j) => j === i ? { ...x, qty: parseFloat(e.target.value) || 0 } : x))}
                              className="h-7 w-16 text-right text-xs mono"
                              data-testid={`comp-manual-qty-${i}`} />
                          </td>
                          <td className="px-3 py-2 text-right text-xs">{m.unit || "pz"}</td>
                          {vediDettagliMargine && (<>
                          <td className="px-3 py-2 text-right bg-amber-50/40">
                            <Input type="number" min={0} step="0.01" value={m.prezzo_acquisto ?? ""}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const npa = parseFloat(e.target.value) || 0;
                                setManualExtras(manualExtras.map((x, j) => {
                                  if (j !== i) return x;
                                  const next = { ...x, prezzo_acquisto: npa };
                                  // Se c'è un ricarico impostato, ricalcola prezzo vendita
                                  if ((Number(x.ricarico) || 0) > 0 && npa > 0) {
                                    next.price = Math.round(npa * Number(x.ricarico) * 100) / 100;
                                  }
                                  return next;
                                }));
                              }}
                              className="h-7 w-20 text-right text-xs mono"
                              placeholder="—"
                              data-testid={`comp-manual-pacq-${i}`} />
                          </td>
                          <td className="px-3 py-2 text-right bg-amber-50/40">
                            <Input type="number" min={0} step="0.01" value={m.ricarico ?? ""}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const nric = parseFloat(e.target.value) || 0;
                                setManualExtras(manualExtras.map((x, j) => {
                                  if (j !== i) return x;
                                  const next = { ...x, ricarico: nric };
                                  if ((Number(x.prezzo_acquisto) || 0) > 0 && nric > 0) {
                                    next.price = Math.round(Number(x.prezzo_acquisto) * nric * 100) / 100;
                                  }
                                  return next;
                                }));
                              }}
                              className="h-7 w-16 text-right text-xs mono"
                              placeholder="—"
                              data-testid={`comp-manual-ric-${i}`} />
                          </td>
                          </>)}
                          <td className="px-3 py-2 text-right">
                            <Input type="number" min={0} step="0.01" value={m.price}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setManualExtras(manualExtras.map((x, j) => j === i ? { ...x, price: parseFloat(e.target.value) || 0 } : x))}
                              className="h-7 w-20 text-right text-xs mono font-semibold"
                              data-testid={`comp-manual-price-${i}`} />
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold">{fmtEur2((m.qty || 0) * (m.price || 0))}</td>
                          <td className="px-3 py-2 text-right"><button onClick={() => setManualExtras(manualExtras.filter((_, j) => j !== i))} className="text-rose-600 text-xs" data-testid={`comp-manual-del-${i}`}>×</button></td>
                        </tr>
                      );})}
                      <tr className="bg-emerald-50 font-bold"><td colSpan={vediDettagliMargine ? 7 : 5} className="px-3 py-2 text-right">Subtotale voci manuali</td><td className="px-3 py-2 text-right font-mono">{fmtEur2(manualExtras.reduce((s, x) => s + (Number(x.qty) || 0) * (Number(x.price) || 0), 0))}</td><td></td></tr>
                    </tbody>
                  </table>
                )}
              </>
            ) : sec ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{sec.name}</h3>
                  <div className="text-sm text-zinc-600">Totale: <span className="font-bold">{fmtEur(sec.voci.reduce((acc, v) => acc + (selections[v.id]?.qty || 0) * v.price, 0))}</span></div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                    <tr><th className="px-3 py-2 text-left">Descrizione</th><th className="px-3 py-2 text-right w-32">Prezzo €/u</th><th className="px-3 py-2 text-center w-12">Sel.</th><th className="px-3 py-2 text-center w-20">Qtà</th><th className="px-3 py-2 text-right w-28">Totale</th></tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {sec.voci.map((v) => {
                      const sel = selections[v.id];
                      const editable = !!v.modificabile_dal_venditore;
                      const effPrice = (sel && editable && typeof sel.price === "number" && sel.price >= 0) ? sel.price : v.price;
                      const listinoCat = detectListinoCategoria(v, sec.id);
                      let prodottiCat = listinoCat ? (prodottiPerCategoria[listinoCat] || []) : [];
                      // Fallback: se zero prodotti nella categoria principale, usa anche le sorelle
                      if (listinoCat && prodottiCat.length === 0 && SISTERS[listinoCat]) {
                        const merged = [];
                        SISTERS[listinoCat].forEach(sCat => {
                          (prodottiPerCategoria[sCat] || []).forEach(p => merged.push(p));
                        });
                        prodottiCat = merged;
                      }
                      return (
                        <tr key={v.id}>
                          <td className="px-3 py-2">
                            <span>{v.name}</span>
                            {editable && <span className="ml-1 text-[9px] uppercase font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 ml-2">prezzo editabile</span>}
                            {!editable && <span className="ml-1 text-[9px] uppercase font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded px-1 ml-2">🔒 lavorazione</span>}
                            {/* SUB-PICKER LISTINI FORNITORI annidato (porte, piastrelle, sanitari, ecc.) */}
                            {sel && prodottiCat.length > 0 && (
                              <div className="mt-1 ml-1 bg-amber-50 border border-amber-300 rounded p-1.5 text-[10px]">
                                <div className="flex items-center gap-1 mb-1">
                                  <span className="font-bold text-amber-900 uppercase tracking-wider">🛒 Scegli prodotto dal listino fornitori ({prodottiCat.length})</span>
                                </div>
                                <select className="w-full h-7 text-[11px] border border-amber-300 rounded px-1 bg-white"
                                  value={sel.product_id || ""}
                                  onChange={(e) => {
                                    const pid = e.target.value;
                                    if (!pid) {
                                      // Torna al prezzo standard della voce
                                      const next = { ...sel }; delete next.product_id; delete next.product_nome; delete next.product_fornitore; delete next.listino_id; next.price = v.price;
                                      setSelections({ ...selections, [v.id]: next });
                                    } else {
                                      const p = prodottiCat.find(x => x.id === pid);
                                      if (p) {
                                        setSelections({ ...selections, [v.id]: {
                                          ...sel,
                                          product_id: p.id, product_nome: p.nome, product_fornitore: p.fornitore_nome || p.listino_nome,
                                          listino_id: p.listino_id,
                                          price: Number(p.prezzo_rivendita) || 0,
                                        }});
                                      }
                                    }
                                  }}
                                  data-testid={`comp-product-${v.id}`}>
                                  <option value="">— Prezzo standard ({fmtEur(v.price)}/{v.unit}) —</option>
                                  {(() => {
                                    const grouped = {};
                                    prodottiCat.forEach(p => { const k = p.fornitore_nome || p.listino_nome || "Senza fornitore"; (grouped[k] = grouped[k] || []).push(p); });
                                    return Object.keys(grouped).sort().map(forn => (
                                      <optgroup key={forn} label={`🏭 ${forn}`}>
                                        {grouped[forn].map(p => (
                                          <option key={p.id} value={p.id}>
                                            {p.codice ? `[${p.codice}] ` : ""}{p.nome} — {fmtEur(p.prezzo_rivendita || 0)}/{p.unit || "pz"}
                                          </option>
                                        ))}
                                      </optgroup>
                                    ));
                                  })()}
                                </select>
                                {sel.product_id && (
                                  <div className="text-[9px] text-amber-800 mt-0.5">
                                    ✓ <strong>{sel.product_nome}</strong> — {sel.product_fornitore} → € {Number(sel.price).toFixed(2)} sostituisce il prezzo standard
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {editable && sel ? (
                              <div className="flex items-center justify-end gap-1">
                                <Input type="number" min={0} step="0.01"
                                  className="h-8 w-24 text-right font-mono"
                                  value={effPrice}
                                  onChange={(e) => setSelections({ ...selections, [v.id]: { ...sel, price: Math.max(0, Number(e.target.value) || 0) } })}
                                  data-testid={`comp-price-${v.id}`}
                                />
                                <span className="text-xs text-zinc-500">/{v.unit}</span>
                              </div>
                            ) : (
                              <span className={editable ? "text-emerald-700" : ""}>{fmtEur(v.price)} /{v.unit}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={!!sel} onChange={(e) => {
                              const s = { ...selections };
                              if (e.target.checked) s[v.id] = { qty: v.unit === "forfait" || v.unit === "pz" ? 1 : mq || 1, price: v.price };
                              else delete s[v.id];
                              setSelections(s);
                            }} data-testid={`comp-check-${v.id}`} />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {sel && <Input type="number" min={0} step="0.5" className="h-8 w-20 mx-auto" value={sel.qty} onChange={(e) => setSelections({ ...selections, [v.id]: { ...sel, qty: Math.max(0, Number(e.target.value) || 0) } })} />}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{sel ? fmtEur((sel.qty || 0) * effPrice) : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : <div className="text-zinc-500 text-center py-12">Seleziona una sezione</div>}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3 sm:p-4 mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">Sicurezza %</Label><Input type="number" min={0} max={100} step="0.5" value={sicurezzaPct} onChange={(e) => setSicurezzaPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></div>
          <div><Label className="text-xs">Dir. Lavori %</Label><Input type="number" min={0} max={100} step="0.5" value={direzionePct} onChange={(e) => setDirezionePct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} /></div>
          <div><Label className="text-xs">Sconto €</Label><Input type="number" min={0} step="1" value={sconto} onChange={(e) => setSconto(Math.max(0, Number(e.target.value) || 0))} /></div>
          <div><Label className="text-xs">IVA %</Label><Input type="number" min={0} max={100} step="0.5" value={ivaPct} onChange={(e) => setIvaPct(Math.max(0, Math.min(100, Number(e.target.value) || 10)))} /></div>
          <Button onClick={save} data-testid="comp-save" style={{ background: "var(--brand)", color: "white" }}><Save className="h-4 w-4 mr-2" />Salva Preventivo</Button>
          {savedId && !isNew && (
            <Button variant="outline" onClick={() => nav(`/preventivocomposite/${savedId || id}/stampa`)} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="comp-stampa">
              <FileText className="h-4 w-4 mr-2" />Anteprima stampa
            </Button>
          )}
        </div>
        <div className="mt-3"><Label className="text-xs">Note</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        {/* Round 90 sexies: modalità di pagamento OPZIONALE nel preventivo.
            Se lasciata vuota → viene mostrato il testo di default (rimando al contratto).
            Se compilata → le rate compaiono in anteprima e PDF. */}
        <div className="bg-white border border-zinc-200 rounded-lg p-4 mt-3">
          <ModalitaPagamentoPicker
            prev={{ modalita_pagamento: modalitaPagamento }}
            setPrev={(updater) => { const next = typeof updater === "function" ? updater({ modalita_pagamento: modalitaPagamento }) : updater; setModalitaPagamento(next.modalita_pagamento || { preset_id: "", label: "", rate: [] }); }}
            totale={totale}
          />
          <div className="text-[10px] text-zinc-500 italic mt-2 leading-snug">
            💡 <strong>Opzionale</strong>: se non imposti rate qui, nel preventivo apparirà il testo standard (le condizioni saranno definite in fase di contratto). Se imposti le rate, verranno stampate nel PDF e potranno essere modificate dopo l'accettazione in <strong>Commessa → Allegato A</strong> (con storico modifiche).
          </div>
        </div>
      </Page>
      <InfissoQuickConfigurator
        open={infissiModalOpen}
        onClose={() => { setInfissiModalOpen(false); setInfissiEditIds(null); }}
        onConfirm={onInfissiConfirm}
        initialItems={infissiEditIds ? infissiExtras.filter((r) => infissiEditIds.includes(r.id)) : null}
        mode={infissiEditIds ? "edit" : "add"}
      />
      {/* Dialog: Aggiungi voce manuale (Round 86) */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="comp-manual-dialog">
          <DialogHeader><DialogTitle>Aggiungi voce extra manuale</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Descrizione *</Label>
              <Input value={manualForm.name} onChange={(e) => {
                const name = e.target.value;
                setManualForm({ ...manualForm, name });
                // Cerca voci backoffice simili (case-insensitive, substring almeno 4 chars)
                if (name.trim().length >= 3) {
                  const needle = name.toLowerCase().trim();
                  const all = [];
                  sections.forEach(s => (s.voci || []).forEach(v => all.push({ ...v, section_id: s.id, section_name: s.name })));
                  const sim = all.filter(v => (v.name || "").toLowerCase().includes(needle) || needle.includes((v.name || "").toLowerCase())).slice(0, 4);
                  setManualSimilar(sim);
                } else setManualSimilar([]);
              }} placeholder="Es. Smaltimento mobilio antico" data-testid="comp-manual-name" />
            </div>
            {manualSimilar.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-2">
                <div className="text-[11px] font-bold text-amber-900 mb-1">💡 Voci simili nel Listino Opere — clicca per usare quella:</div>
                <div className="space-y-1">
                  {manualSimilar.map(v => (
                    <button key={v.id} onClick={() => {
                      // Usa la voce esistente: vai alla sua sezione e setta qty
                      setActiveSection(v.section_id);
                      setSelections({ ...selections, [v.id]: { qty: manualForm.qty || 1, price: v.price } });
                      setManualDialogOpen(false);
                      toast.success(`Usata "${v.name}" da ${v.section_name}`);
                    }} className="w-full text-left text-xs bg-white border border-amber-200 hover:border-amber-500 rounded px-2 py-1" data-testid={`comp-manual-use-${v.id}`}>
                      ✓ <strong>{v.name}</strong> — {v.category} · {fmtEur(v.price)}/{v.unit}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Categoria</Label><Input value={manualForm.category} onChange={(e) => setManualForm({ ...manualForm, category: e.target.value.toUpperCase() })} placeholder="EXTRA" data-testid="comp-manual-cat" /></div>
              <div><Label className="text-xs">Unità</Label>
                <select className="w-full h-10 border border-zinc-300 rounded px-2 text-sm" value={manualForm.unit} onChange={(e) => setManualForm({ ...manualForm, unit: e.target.value })} data-testid="comp-manual-unit">
                  <option value="pz">pz</option><option value="m²">m²</option><option value="ml">ml</option><option value="m³">m³</option><option value="ora">ora</option><option value="gg">gg</option><option value="corpo">corpo</option><option value="forfait">forfait</option>
                </select>
              </div>
              <div><Label className="text-xs">Quantità</Label><Input type="number" min="0" step="0.01" value={manualForm.qty} onChange={(e) => setManualForm({ ...manualForm, qty: parseFloat(e.target.value) || 0 })} data-testid="comp-manual-qty" /></div>
            </div>
            {/* R89 quinquies: prezzo acquisto + ricarico (SOLO admin / area_manager) */}
            {(isAdmin || (userLevel === "area_manager")) && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold text-amber-800">💰 Calcolo margine (opzionale)</span>
                <span className="text-[10px] text-amber-600 italic">— utile per la marginalità interna</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">Prezzo di acquisto €</Label>
                  <Input type="number" min="0" step="0.01" value={manualForm.prezzo_acquisto ?? ""}
                    placeholder="es. 100"
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const pa = parseFloat(e.target.value) || 0;
                      const next = { ...manualForm, prezzo_acquisto: pa };
                      // Auto-calcola prezzo vendita se ricarico è impostato
                      if ((Number(manualForm.ricarico) || 0) > 0 && pa > 0) {
                        next.price = Math.round(pa * Number(manualForm.ricarico) * 100) / 100;
                      }
                      setManualForm(next);
                    }}
                    data-testid="comp-manual-dlg-pacq" />
                </div>
                <div>
                  <Label className="text-[10px]">Ricarico (es. 1.8)</Label>
                  <Input type="number" min="0" step="0.01" value={manualForm.ricarico ?? ""}
                    placeholder="es. 1.8"
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const ric = parseFloat(e.target.value) || 0;
                      const next = { ...manualForm, ricarico: ric };
                      if ((Number(manualForm.prezzo_acquisto) || 0) > 0 && ric > 0) {
                        next.price = Math.round(Number(manualForm.prezzo_acquisto) * ric * 100) / 100;
                      }
                      setManualForm(next);
                    }}
                    data-testid="comp-manual-dlg-ric" />
                </div>
                <div>
                  <Label className="text-[10px] text-emerald-700 font-semibold">Margine unit.</Label>
                  <div className="h-10 flex items-center justify-end px-2 bg-white border border-emerald-200 rounded font-mono text-emerald-700 font-bold text-sm">
                    {(() => {
                      const pa = Number(manualForm.prezzo_acquisto) || 0;
                      const pv = Number(manualForm.price) || 0;
                      if (pa <= 0 || pv <= 0) return "—";
                      const m = pv - pa;
                      const pct = ((m / pv) * 100).toFixed(1);
                      return `${fmtEur2(m)} (${pct}%)`;
                    })()}
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-amber-700 mt-1.5 italic">
                💡 Se compili prezzo acquisto + ricarico, il prezzo di vendita si aggiorna automaticamente. Puoi comunque forzare manualmente il prezzo qui sotto.
              </div>
            </div>
            )}
            <div><Label className="text-xs">Prezzo unitario di vendita €</Label><Input type="number" min="0" step="0.01" value={manualForm.price} onChange={(e) => setManualForm({ ...manualForm, price: parseFloat(e.target.value) || 0 })} data-testid="comp-manual-price" /></div>
            <div className="text-sm font-bold text-right">Totale: {fmtEur2((Number(manualForm.qty) || 0) * (Number(manualForm.price) || 0))}</div>
            {canSaveToBackoffice ? (
              <label className="flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded p-2 cursor-pointer">
                <input type="checkbox" checked={!!manualForm.save_to_backoffice} onChange={(e) => setManualForm({ ...manualForm, save_to_backoffice: e.target.checked })} data-testid="comp-manual-save-bo" />
                <div className="text-xs">
                  <strong>Salva anche nelle Voci Backoffice</strong>
                  <div className="text-[10px] text-zinc-600">Sarà disponibile come voce standard per i prossimi preventivi (modificabile dal venditore: ON).</div>
                </div>
              </label>
            ) : (
              <div className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2">
                ℹ️ Questa voce sarà aggiunta solo a questo preventivo. Per renderla riusabile chiedi a un admin di salvarla nelle Voci Backoffice.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Annulla</Button>
            <Button onClick={async () => {
              if (!manualForm.name.trim()) { toast.error("Descrizione obbligatoria"); return; }
              if ((Number(manualForm.price) || 0) <= 0) { toast.error("Prezzo > 0"); return; }
              let saved_to_backoffice = false;
              let voce_id_bo = null;
              if (canSaveToBackoffice && manualForm.save_to_backoffice) {
                try {
                  const rivendita = Number(manualForm.price) || 0;
                  // R89 quinquies: se l'utente ha inserito prezzo_acquisto + ricarico reali, usali. Altrimenti calcolo stimato.
                  const acquisto = Number(manualForm.prezzo_acquisto) > 0
                    ? Number(manualForm.prezzo_acquisto)
                    : Math.round((rivendita / 1.8) * 100) / 100;
                  const ricarico = Number(manualForm.ricarico) > 0
                    ? Number(manualForm.ricarico)
                    : (acquisto > 0 ? Math.round((rivendita / acquisto) * 100) / 100 : 1.8);
                  const { data: newVoce } = await api.post("/voci-backoffice", {
                    name: manualForm.name.trim(),
                    category: manualForm.category || "EXTRA",
                    unit: manualForm.unit,
                    prezzo_acquisto: acquisto,
                    ricarico: ricarico,
                    prezzo_rivendita: rivendita,
                    modificabile_dal_venditore: true,
                    cad_category: null,
                  });
                  saved_to_backoffice = true; voce_id_bo = newVoce?.id || null;
                  toast.success("Voce salvata nel Listino Opere");
                } catch (e) { toast.error("Impossibile salvare voce nel backoffice: " + (e?.response?.data?.detail || e.message)); }
              }
              setManualExtras([...manualExtras, {
                name: manualForm.name.trim(),
                category: manualForm.category || "EXTRA",
                unit: manualForm.unit,
                qty: Number(manualForm.qty) || 0,
                price: Number(manualForm.price) || 0,
                prezzo_acquisto: Number(manualForm.prezzo_acquisto) || null,
                ricarico: Number(manualForm.ricarico) || null,
                manual: true,
                saved_to_backoffice,
                voce_id_bo,
              }]);
              setManualDialogOpen(false);
              toast.success("Voce aggiunta al preventivo");
            }} data-testid="comp-manual-confirm" style={{ background: "var(--brand)", color: "white" }}>Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ListinoProdottoPicker
        open={listinoPickerOpen}
        onOpenChange={setListinoPickerOpen}
        defaultCategoria={listinoPickerCat}
        onConfirm={(items) => {
          setListiniSelections(ls => {
            const out = [...ls];
            items.forEach(it => {
              // Se già presente con stesso listino_id+id → somma qty
              const idx = out.findIndex(x => x.listino_id === it.listino_id && x.id === it.id);
              if (idx >= 0) { out[idx] = { ...out[idx], qty: (parseFloat(out[idx].qty) || 0) + (parseFloat(it.qty) || 0) }; }
              else out.push(it);
            });
            return out;
          });
          toast.success(`${items.length} prodotto/i aggiunto/i ai listini`);
        }}
      />
      {/* Dialog: richiesta sconto > 5% per autorizzazione admin */}
      <Dialog open={scontoDialog} onOpenChange={setScontoDialog}>
        <DialogContent className="max-w-md" data-testid="comp-sconto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-600" /> Richiesta sconto maggiore del {SOGLIA_SCONTO_AUTO}%</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded text-xs text-amber-900">Per applicare uno sconto superiore al {SOGLIA_SCONTO_AUTO}% serve l'autorizzazione dell'admin. Riceverai notifica appena viene presa una decisione.</div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">% sconto richiesta</Label>
              <Input type="number" min={SOGLIA_SCONTO_AUTO + 0.5} max={100} step="0.5" value={scontoForm.pct} onChange={(e) => setScontoForm(s => ({ ...s, pct: parseFloat(e.target.value) || 0 }))} className="mono mt-1" data-testid="comp-sconto-req-pct" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Motivazione (obbligatoria)</Label>
              <Textarea value={scontoForm.motivo} onChange={(e) => setScontoForm(s => ({ ...s, motivo: e.target.value }))} placeholder="Es: cliente in trattativa, fidelizzazione, opportunità referral..." rows={4} className="mt-1" data-testid="comp-sconto-req-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScontoDialog(false)}>Annulla</Button>
            <Button disabled={scontoForm.pct <= SOGLIA_SCONTO_AUTO || !scontoForm.motivo.trim() || (!savedId && isNew)}
              style={{ background: "#0F172A", color: "white" }}
              onClick={async () => {
                try {
                  const targetId = savedId || id;
                  const { data } = await api.post(`/preventivi/${targetId}/sconto-richiesta`, { pct: scontoForm.pct, motivo: scontoForm.motivo.trim() });
                  setScontoReq(data);
                  setScontoDialog(false);
                  toast.success(`Richiesta inviata all'admin (${data.pct_richiesto}%)`);
                } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
              }}
              data-testid="comp-sconto-req-send"
            >Invia richiesta all'admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Round 90: Dialog "preventivo già accettato" — 3 scelte per gestire varianti in corso d'opera */}
      <Dialog open={acceptedDialog} onOpenChange={setAcceptedDialog}>
        <DialogContent className="max-w-lg" data-testid="comp-accepted-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Questo preventivo è già stato ACCETTATO</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-xs text-emerald-900 leading-snug">
              Il preventivo <strong>{savedId || id}</strong> risulta <strong>accettato</strong>{prevCommessa ? <> ed è collegato alla commessa <strong>{prevCommessa.codice}</strong></> : ""}.
              Modificarlo direttamente può alterare un documento firmato. Scegli come vuoi procedere:
            </div>
            {prevCommessa && (
              <button
                onClick={async () => {
                  setCloning(true);
                  try {
                    const { data } = await api.post(`/commesse/${prevCommessa.id}/preventivi/clone-from/${savedId || id}`, { titolo: `Variante in corso d'opera del ${new Date().toLocaleDateString("it-IT")}` });
                    toast.success("Preventivo Extra creato: modifica pure senza toccare l'originale");
                    setAcceptedDialog(false);
                    setAcceptedChoice("extra");
                    // Naviga al nuovo preventivo extra
                    nav(`/preventivocomposite/${data.id}`, { replace: true });
                    window.location.reload();
                  } catch (e) {
                    toast.error("Errore clonazione: " + (e?.response?.data?.detail || e.message));
                  } finally { setCloning(false); }
                }}
                disabled={cloning}
                className="w-full text-left border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 rounded p-3 transition"
                data-testid="comp-accepted-choice-extra"
              >
                <div className="font-bold text-amber-900 mb-0.5">➕ Crea preventivo EXTRA (variante in corso d'opera)</div>
                <div className="text-[11px] text-amber-800 leading-snug">
                  Consigliato: crea un NUOVO preventivo collegato alla stessa commessa <strong>{prevCommessa.codice}</strong>.
                  L'originale accettato resta inalterato. L'extra andrà firmato a parte prima dell'esecuzione.
                </div>
              </button>
            )}
            <button
              onClick={() => {
                setAcceptedDialog(false);
                setAcceptedChoice("nuovo");
                // Crea un nuovo preventivo indipendente con cliente precompilato
                nav("/preventivocomposite/new", { state: { cliente } });
                // Piccolo hint: mantieni cliente pre-compilato viene già fatto da state, ma
                // per PreventivoComposite bisognerebbe supportare useLocation. Semplicemente naviga.
                window.location.href = "/preventivocomposite/new";
              }}
              className="w-full text-left border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 rounded p-3 transition"
              data-testid="comp-accepted-choice-nuovo"
            >
              <div className="font-bold text-blue-900 mb-0.5">🆕 Crea preventivo NUOVO indipendente</div>
              <div className="text-[11px] text-blue-800 leading-snug">
                Nuovo lavoro slegato dalla commessa attuale. Utile se il cliente ti ha chiesto un secondo intervento non correlato.
              </div>
            </button>
            <button
              onClick={() => {
                setAcceptedDialog(false);
                setAcceptedChoice("modifica");
                toast.warning("Modifichi il preventivo accettato: qualsiasi salvataggio sovrascrive il documento firmato.");
              }}
              className="w-full text-left border-2 border-rose-300 bg-rose-50 hover:bg-rose-100 rounded p-3 transition"
              data-testid="comp-accepted-choice-modifica"
            >
              <div className="font-bold text-rose-900 mb-0.5">✏️ Modifica il preventivo originale (rischioso)</div>
              <div className="text-[11px] text-rose-800 leading-snug">
                Sovrascrive il preventivo già firmato dal cliente. Usa solo per errori manifesti (typo, IVA, ecc.).
                Ne verrà comunque salvata una copia in cronologia (snapshot).
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptedDialog(false)} data-testid="comp-accepted-cancel">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
